import React from "react";

const money = (value) =>
  value === undefined || value === null
    ? "-"
    : `KD ${Number(value).toFixed(3)}`;

/**
 * What the plan will actually cost and what each item will redeem at, answered by the
 * service rather than worked out here - list prices live in the catalog, so the dashboard
 * is in no position to price anything itself.
 */
const PricingPanel = ({ pricing, isLoading, perPlan = false }) => {
  const lines = pricing?.lines ?? [];
  const errors = pricing?.errors ?? [];
  const remainder = Number(pricing?.reconciliation_remainder ?? 0);

  if (!pricing || Object.keys(pricing).length === 0) {
    return (
      <div className="pro-p-4 pro-mb-4 pro-bg-light rounded-3">
        <p className="pro-mb-0 pro-font-sm">
          Choose an item and a price to see what this plan is worth.
        </p>
      </div>
    );
  }

  /**
   * Several sizes are several plans, so what matters is each line's own figures rather than
   * the totals beside them: a combined list value here would be the cost of buying every size
   * at once, which is a price nobody is ever charged.
   */
  if (perPlan) {
    return (
      <div className="pro-p-4 pro-mb-4 pro-bg-light rounded-3">
        <div className="pro-d-flex pro-justify-between pro-items-center pro-mb-3">
          <h6 className="pro-ttl pro-mb-0">
            Pricing — {lines.length} plan{lines.length === 1 ? "" : "s"}
          </h6>
          {isLoading && <span className="pro-font-sm">Pricing…</span>}
        </div>

        {errors?.length > 0 && (
          <div className="pro-mb-3">
            {errors.map((error) => (
              <p key={error} className="error-text pro-mb-1">
                {error}
              </p>
            ))}
          </div>
        )}

        {/*
          Printed above the sizes only while they actually share a figure. Priced size by size
          there is no one number to put here, and the discount travels with the size instead.
        */}
        {!pricing?.per_size && (
          <div className="pro-d-flex pro-justify-between">
            <span className="pro-font-sm">Discount, shared by every size</span>
            <span className="pro-font-sm pro-fw-medium">
              {pricing?.discount_percent ?? 0}%
            </span>
          </div>
        )}

        {lines.map((line) => {
          const quantity = Number(line?.quantity ?? 0);
          // The service prices each size when they differ, so its figure is preferred over
          // one worked out here; the multiply is the fallback for a set sharing a discount.
          const sellingPrice =
            line?.selling_price ?? quantity * Number(line?.discounted_price ?? 0);

          return (
            <div
              key={line?.line_id ?? line?.variant_id ?? line?.name}
              className="pro-pt-3 pro-mt-3 pro-border-top"
            >
              <div className="pro-d-flex pro-justify-between pro-mb-1">
                <span className="pro-font-sm pro-fw-medium">{line?.name}</span>
                <span className="pro-font-sm pro-fw-bolder">
                  {money(sellingPrice)}
                </span>
              </div>
              <div className="pro-d-flex pro-justify-between pro-gap-3">
                <span className="pro-font-xs">
                  {quantity} × {money(line?.list_price)} ={" "}
                  {money(line?.line_list_value)} list
                </span>
                <span className="pro-font-xs pro-text-end">
                  {money(line?.discounted_price)} each on redemption
                </span>
              </div>
              {pricing?.per_size && (
                <div className="pro-d-flex pro-justify-between pro-gap-3">
                  <span className="pro-font-xs">
                    {line?.discount_percent ?? 0}% off
                  </span>
                  <span className="pro-font-xs pro-text-end">
                    saves {money(line?.discount_amount)}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="pro-p-4 pro-mb-4 pro-bg-light rounded-3">
      <div className="pro-d-flex pro-justify-between pro-items-center pro-mb-3">
        <h6 className="pro-ttl pro-mb-0">Pricing</h6>
        {isLoading && <span className="pro-font-sm">Pricing…</span>}
      </div>

      {errors?.length > 0 && (
        <div className="pro-mb-3">
          {errors.map((error) => (
            <p key={error} className="error-text pro-mb-1">
              {error}
            </p>
          ))}
        </div>
      )}

      <div className="pro-d-flex pro-justify-between pro-mb-1">
        <span className="pro-font-sm">List value</span>
        <span className="pro-font-sm pro-fw-medium">
          {money(pricing?.list_value)}
        </span>
      </div>
      <div className="pro-d-flex pro-justify-between pro-mb-1">
        <span className="pro-font-sm">Discount</span>
        <span className="pro-font-sm pro-fw-medium">
          {pricing?.discount_percent ?? 0}% ({money(pricing?.discount_amount)})
        </span>
      </div>
      <div className="pro-d-flex pro-justify-between pro-mb-3">
        <span className="pro-font-sm pro-fw-medium">Selling price</span>
        <span className="pro-font-sm pro-fw-bolder">
          {money(pricing?.selling_price)}
        </span>
      </div>

      {lines?.length > 0 && (
        <div className="pro-pt-3 pro-border-top">
          <p className="pro-font-sm pro-fw-medium pro-mb-2">
            Redemption price per item
          </p>
          {lines.map((line) => (
            <div
              key={line?.line_id ?? line?.product_id}
              className="pro-d-flex pro-justify-between pro-mb-1"
            >
              <span className="pro-font-sm">
                {line?.name} × {line?.quantity}
              </span>
              <span className="pro-font-sm">
                {money(line?.list_price)} → {money(line?.discounted_price)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/*
        Lines round to the fils independently, so an awkward percentage can leave the plan
        price and the sum of its items a few fils apart. Deterministic POS figures were
        chosen over exact reconciliation, so this is shown rather than corrected.
      */}
      {remainder !== 0 && (
        <div className="pro-mt-3 pro-pt-3 pro-border-top">
          <p className="pro-font-sm pro-mb-0">
            <span className="material-symbols-outlined pro-align-middle">
              info
            </span>{" "}
            The items reconcile to {money(remainder)} {remainder > 0
              ? "more"
              : "less"}{" "}
            than the selling price. Item prices are kept exact for the POS.
          </p>
        </div>
      )}
    </div>
  );
};

export default PricingPanel;
